import Vue from "vue";
import VueI18n from "vue-i18n";
import ApiService from "@/core/services/api.service";
import moment from "moment";
import SecureLS from "secure-ls";

const ls = new SecureLS({ encodingType: "aes", isCompression: true });

Vue.use(VueI18n);

// --- i18n cache (localStorage, sürüm bazlı tek anahtar) ---
const APP_VERSION = process.env.VUE_APP_VERSION || "1";
const CACHE_KEY = `i18n_translations_cache_v${APP_VERSION}`;
const CACHE_KEY_PREFIX = "i18n_translations_cache_v";
const MAX_CACHE_MB = Number(process.env.VUE_APP_I18N_CACHE_MAX_MB) || 2;
const MAX_CACHE_BYTES = MAX_CACHE_MB * 1024 * 1024;

function isValidTranslationData(data) {
  return data && Array.isArray(data.languages) && data.translations && typeof data.translations === "object";
}

/** Eski sürüm cache anahtarlarını siler; sadece mevcut sürümün anahtarı kalır. */
function cleanupOldCacheKeys() {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(CACHE_KEY_PREFIX) && key !== CACHE_KEY)
      .forEach((key) => localStorage.removeItem(key));
  } catch (_) {}
}

function getCachedTranslations() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return isValidTranslationData(data) ? data : null;
  } catch (_) {
    return null;
  }
}

function setCachedTranslations(data) {
  if (!isValidTranslationData(data)) return;
  try {
    const str = JSON.stringify(data);
    if (str.length > MAX_CACHE_BYTES) {
      console.warn(`[i18n] Cache atlandı: ${(str.length / 1024 / 1024).toFixed(2)} MB > ${MAX_CACHE_MB} MB`);
      return;
    }
    localStorage.setItem(CACHE_KEY, str);
  } catch (e) {
    console.warn("i18n cache set failed:", e);
  }
}

function clearCachedTranslations() {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (_) {}
}

function getCachedTranslationsDebug() {
  const data = getCachedTranslations();
  if (!data) return null;
  const size = JSON.stringify(data).length;
  return {
    sizeBytes: size,
    sizeMB: (size / 1024 / 1024).toFixed(2),
    languages: data.languages.length,
    localeKeys: Object.keys(data.translations),
    raw: data,
  };
}

// --- API ve çeviri verisi işleme ---
function getTranslations() {
  return ApiService.get("/api/localization");
}

const momentLocaleConfig = {
  relativeTime: {
    future: "%s sonra",
    past: "%s önce",
    s: "%d saniye",
    m: "%d dakika",
    mm: "%d dakika",
    h: "%d saat",
    hh: "%d saat",
    d: "%d gün",
    dd: "%d gün",
    M: "%d ay",
    MM: "%d ay",
    y: "%d yıl",
    yy: "%d yıl",
  },
};

/** API cevabını i18n messages + locale'e çevirir; xlanguages, moment ve dil tercihini günceller. */
function applyTranslationsData(data) {
  if (!isValidTranslationData(data)) return { messages: {}, locale: "tr" };

  const messages = {};
  const languages = data.languages;
  ls.set("xlanguages", JSON.stringify(languages));

  languages.forEach((lang) => {
    Vue.set(messages, lang.code, data.translations[lang.code] || {});
  });

  let locale = "tr";
  try {
    locale = ls.get("language") || locale;
  } catch (_) {
    try { ls.remove("language"); } catch (__) {}
  }
  moment.locale(locale, momentLocaleConfig);

  return { messages, locale };
}

/** Mevcut bir i18n instance'ını API cevabıyla günceller (canlı güncelleme için). */
function updateI18nInstance(i18nInstance, data) {
  const { messages, locale } = applyTranslationsData(data);
  Object.keys(messages).forEach((loc) => i18nInstance.setLocaleMessage(loc, messages[loc]));
  i18nInstance.locale = locale;
}

function prepareConfig(data) {
  const { messages, locale } = applyTranslationsData(data);
  return new VueI18n({
    locale,
    messages,
    fallbackLocale: "tr",
  });
}

export {
  prepareConfig,
  getTranslations,
  applyTranslationsData,
  updateI18nInstance,
  getCachedTranslations,
  setCachedTranslations,
  getCachedTranslationsDebug,
  clearCachedTranslations,
  cleanupOldCacheKeys,
};
